import DateFnsUtils from '@date-io/date-fns';
import { FormHelperText } from '@material-ui/core';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import React, { useEffect, useRef, useState } from 'react';
import { DISPLAY_DATE_FORMAT } from './constants';
import { transformInput, transformOutput, validate } from './utils';

export type Props = {
  data?: fhir.date;
  disabled?: boolean;
  disableFuture?: boolean;
  disablePast?: boolean;
  label?: string;
  maxDate?: string;
  minDate?: string;
  name: string;
  onChange: (data: fhir.date, name: string) => void;
  onValidate: (isValid: boolean) => void;
  required?: boolean;
  timeZone?: string;
  useUtcCalendarDate?: boolean;
  representation?: 'date' | 'complete';
  renderDay?: (
    day: Date | null,
    selectedDate: Date | null,
    dayInCurrentMonth: boolean,
    dayComponent: React.ReactElement
  ) => React.ReactElement;
  variant?: 'dialog' | 'inline' | 'static';
  inputVariant?: 'standard' | 'outlined' | 'filled';
  className?: string;
  placeHolder?: string;
  minDateMessage?: string;
  maxDateMessage?: string;
  invalidDateMessage?: string;
};

const Date: React.FC<Props> = ({
  data,
  disabled,
  disableFuture,
  disablePast,
  label,
  maxDate,
  minDate = '1900-01-01',
  name,
  onChange,
  onValidate,
  required,
  timeZone,
  useUtcCalendarDate,
  representation = 'date',
  renderDay,
  variant,
  inputVariant = 'filled',
  className = '',
  placeHolder = '',
  minDateMessage = '',
  maxDateMessage = '',
  invalidDateMessage = '',
}) => {
  const [showErrors, setShowErrors] = useState<any>({});
  const prevValidationResult = useRef<boolean | null>(null);

  const handleChange = (date: any, value?: string | null) => {
    onChange(
      transformOutput(date, value || '', timeZone, representation),
      name
    );
  };

  useEffect(() => {
    const errors = validate(data, required, minDate, maxDate);
    const isValid = Object.keys(errors).length < 1;

    if (prevValidationResult.current !== isValid) {
      prevValidationResult.current = isValid;
      onValidate(isValid);
    }
  }, [data, required, minDate, maxDate, onValidate]);

  const value = transformInput(data, timeZone, useUtcCalendarDate);
  const errors = validate(data, required, minDate, maxDate);

  const getError = (name: string) => {
    return showErrors?.[name] && !!errors?.[name] ? errors?.[name] || '' : '';
  };

  const handleBlur = (name: string) => () => {
    setShowErrors({
      ...showErrors,
      [name]: false,
    });
  };

  const handleFocus = (name: string) => () => {
    setShowErrors({
      ...showErrors,
      [name]: false,
    });
  };

  const dateError = getError('date');

  return (
    <>
      <MuiPickersUtilsProvider utils={DateFnsUtils}>
        <KeyboardDatePicker
          autoOk
          required={required}
          format={DISPLAY_DATE_FORMAT}
          fullWidth
          inputVariant={inputVariant}
          label={label}
          name={name}
          disablePast={disablePast}
          disableFuture={disableFuture}
          onChange={handleChange}
          size="small"
          InputProps={{
            autoComplete: 'off',
            error: !!dateError,
            onBlur: handleBlur('date'),
            onFocus: handleFocus('date'),
          }}
          minDate={minDate}
          maxDate={maxDate}
          minDateMessage={minDateMessage}
          maxDateMessage={maxDateMessage}
          invalidDateMessage={invalidDateMessage}
          value={value ? value : null}
          placeholder={placeHolder ? placeHolder : DISPLAY_DATE_FORMAT}
          disabled={disabled}
          data-testid={`date-picker-${name}`}
          {...(renderDay
            ? {
              renderDay: renderDay,
            }
            : {})}
          {...(variant
            ? {
              variant: variant,
            }
            : {})}
          className={className}
        />
      </MuiPickersUtilsProvider>
      {dateError && (
        <FormHelperText error={!!dateError}>{dateError}</FormHelperText>
      )}
    </>
  );
};

export default React.memo(Date);
